import { Vector3 } from '@/math'
import type { VoxelReader } from './voxelReaders'
import { Position, posKeyXYZ } from './voxelMath'

export interface PickResult {
  position: Position
  normal: Vector3
  steps: number
  exactPoint: Vector3  // Exact intersection point on voxel surface
}

/**
 * Fast 3D DDA (Digital Differential Analyzer) voxel picking.
 * Traverses the voxel grid along a ray to find the first occupied voxel.
 *
 * Based on "A Fast Voxel Traversal Algorithm for Ray Tracing" by Amanatides & Woo
 *
 * When `bounds` is given the ray is first clipped to the document box
 * `[0, bounds.x) x [0, bounds.y) x [0, bounds.z)` with a slab test. A ray that
 * misses the box returns `null` without stepping, and a ray that starts outside
 * begins stepping at the entry point, so `maxSteps` counts steps inside the box.
 * The effective cap is raised to at least the box's Manhattan extent so a
 * diagonal ray through a 512-per-side box can never be truncated; `maxSteps`
 * stays a safety net against unbounded traversal.
 *
 * The loop allocates nothing; only the returned hit is allocated.
 */
export function pickVoxel(
  rayOrigin: Vector3,
  rayDirection: Vector3,
  voxels: VoxelReader,
  bounds?: Position | null,
  maxSteps: number = 1000
): PickResult | null {
  const ox = rayOrigin.x
  const oy = rayOrigin.y
  const oz = rayOrigin.z

  // Normalize ray direction (same arithmetic as Vector3#normalize)
  let rdx = rayDirection.x
  let rdy = rayDirection.y
  let rdz = rayDirection.z
  const invLen = 1 / (Math.sqrt(rdx * rdx + rdy * rdy + rdz * rdz) || 1)
  rdx *= invLen
  rdy *= invLen
  rdz *= invLen

  // Avoid division by zero
  const epsilon = 1e-9
  if (Math.abs(rdx) < epsilon) rdx = epsilon
  if (Math.abs(rdy) < epsilon) rdy = epsilon
  if (Math.abs(rdz) < epsilon) rdz = epsilon

  // Step direction (+1 or -1 for each axis)
  const stepX = rdx > 0 ? 1 : -1
  const stepY = rdy > 0 ? 1 : -1
  const stepZ = rdz > 0 ? 1 : -1

  // Distance between voxel boundaries along the ray
  const tDeltaX = Math.abs(1 / rdx)
  const tDeltaY = Math.abs(1 / rdy)
  const tDeltaZ = Math.abs(1 / rdz)

  // Track which face was crossed to enter the voxel (for normal calculation)
  let lastAxis = -1 // 0=X, 1=Y, 2=Z
  let lastStep = 0

  // Clip the ray to the bounds box so stepping begins at the entry point.
  // tStart is a ray parameter measured from rayOrigin (0 when already inside).
  let tStart = 0
  let bx = 0
  let by = 0
  let bz = 0
  let limit = maxSteps
  if (bounds) {
    bx = bounds.x
    by = bounds.y
    bz = bounds.z

    const invX = 1 / rdx
    const invY = 1 / rdy
    const invZ = 1 / rdz
    let tx0 = -ox * invX
    let tx1 = (bx - ox) * invX
    if (tx0 > tx1) { const t = tx0; tx0 = tx1; tx1 = t }
    let ty0 = -oy * invY
    let ty1 = (by - oy) * invY
    if (ty0 > ty1) { const t = ty0; ty0 = ty1; ty1 = t }
    let tz0 = -oz * invZ
    let tz1 = (bz - oz) * invZ
    if (tz0 > tz1) { const t = tz0; tz0 = tz1; tz1 = t }

    let tMin = tx0
    let enterAxis = 0
    if (ty0 > tMin) { tMin = ty0; enterAxis = 1 }
    if (tz0 > tMin) { tMin = tz0; enterAxis = 2 }
    const tMax = Math.min(tx1, ty1, tz1)
    // Box is missed, or lies entirely behind the origin
    if (tMin > tMax || tMax < 0) return null
    if (tMin > 0) {
      tStart = tMin
      // The ray enters through the face on `enterAxis`; if the entry voxel is
      // occupied that is the face hit, exactly as if we had stepped across it.
      lastAxis = enterAxis
      lastStep = enterAxis === 0 ? stepX : enterAxis === 1 ? stepY : stepZ
    }

    // Inside the box a DDA visits at most bx + by + bz voxels (plus a few
    // boundary-rounding steps), so never let the cap truncate the traversal.
    const extent = bx + by + bz + 3
    if (extent > limit) limit = extent
  }

  // Starting point and voxel
  const px = tStart === 0 ? ox : ox + rdx * tStart
  const py = tStart === 0 ? oy : oy + rdy * tStart
  const pz = tStart === 0 ? oz : oz + rdz * tStart
  let vx = Math.floor(px)
  let vy = Math.floor(py)
  let vz = Math.floor(pz)

  // Initial tMax values: ray parameter (from rayOrigin) of the next voxel
  // boundary on each axis. Only their relative order matters for stepping.
  let tMaxX = Math.abs((vx + (rdx > 0 ? 1 : 0) - ox) / rdx)
  let tMaxY = Math.abs((vy + (rdy > 0 ? 1 : 0) - oy) / rdy)
  let tMaxZ = Math.abs((vz + (rdz > 0 ? 1 : 0) - oz) / rdz)

  for (let steps = 0; steps < limit; steps++) {
    // If bounds provided, limit traversal to it; otherwise allow unbounded search
    if (!bounds || (
      vx >= 0 && vy >= 0 && vz >= 0 &&
      vx < bx && vy < by && vz < bz
    )) {
      // Check if voxel is occupied
      if (voxels.has(posKeyXYZ(vx, vy, vz))) {
        // Calculate normal based on which face we entered from
        const normal = new Vector3(0, 0, 0)
        if (lastAxis === 0) normal.x = -lastStep
        else if (lastAxis === 1) normal.y = -lastStep
        else if (lastAxis === 2) normal.z = -lastStep

        // Calculate exact intersection point on the voxel face we entered through
        let t: number
        if (lastAxis === 0) {
          const faceX = vx + (lastStep < 0 ? 1 : 0)
          t = (faceX - ox) / rdx
        } else if (lastAxis === 1) {
          const faceY = vy + (lastStep < 0 ? 1 : 0)
          t = (faceY - oy) / rdy
        } else {
          const faceZ = vz + (lastStep < 0 ? 1 : 0)
          t = (faceZ - oz) / rdz
        }
        const exactPoint = new Vector3(ox + rdx * t, oy + rdy * t, oz + rdz * t)

        return {
          position: { x: vx, y: vy, z: vz },
          normal,
          steps,
          exactPoint
        }
      }
    } else {
      // If we're outside bounds and moving away, stop
      if ((vx < 0 && stepX < 0) || (vx >= bx && stepX > 0)) break
      if ((vy < 0 && stepY < 0) || (vy >= by && stepY > 0)) break
      if ((vz < 0 && stepZ < 0) || (vz >= bz && stepZ > 0)) break
    }

    // Move to next voxel boundary
    if (tMaxX < tMaxY && tMaxX < tMaxZ) {
      vx += stepX
      tMaxX += tDeltaX
      lastAxis = 0
      lastStep = stepX
    } else if (tMaxY < tMaxZ) {
      vy += stepY
      tMaxY += tDeltaY
      lastAxis = 1
      lastStep = stepY
    } else {
      vz += stepZ
      tMaxZ += tDeltaZ
      lastAxis = 2
      lastStep = stepZ
    }
  }

  return null
}
